"use client";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import CameraEnhanceIcon from "@mui/icons-material/CameraEnhance";
import CloseIcon from "@mui/icons-material/Close";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Webcam from "react-webcam";
import { processImage, processIdentityCard, processDrivingLicenseBothSides } from "../Utils/ProcessImages";
import { Vocabulary } from "../Utils/Vocabulary";
import styles from "./ScanIdModal.module.css";
import { ScanDocumentType } from "../Utils/Constants";
import { isMobile, isTablet } from "../Utils/Utils";

interface ScanIdProps {
  openModal: boolean;
  handleClose: () => void;
  doneProcess: (data: any) => void;
  documentType?: string;
}

type CameraSideType = "user" | "environment";

export default function ScanId(props: ScanIdProps) {
  const {
    openModal,
    handleClose,
    doneProcess,
    documentType = ScanDocumentType.ID,
  } = props;
  const [image, setImage] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [frontData, setFrontData] = useState<any>(null);
  const [isProcessingBack, setIsProcessingBack] = useState(false);
  const [isNewIdModel, setIsNewIdModel] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const [showCamera, setShowCamera] = useState(true);
  const [cameraSide, setCameraSide] = useState<CameraSideType>(() =>
    isMobile() || isTablet() ? "environment" : "user"
  );
  const [backCameras, setBackCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentBackCameraIndex, setCurrentBackCameraIndex] =
    useState<number>(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(
    undefined
  );
  const [isCardDetected, setIsCardDetected] = useState(false);
  const [frontCapturedPreview, setFrontCapturedPreview] = useState<string | null>(null);
  const isMobileDevice = isMobile() || isTablet();
  const notifySuccess = (message: string) => toast.success(message);

  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === "videoinput");
      const backCams = videoDevices.filter(
        (device) =>
          device.label.toLowerCase().includes("back") ||
          device.label.toLowerCase().includes("rear") ||
          device.label.toLowerCase().includes("environment") ||
          (isMobileDevice && device.label !== "" && !device.label.toLowerCase().includes("front"))
      );
      setBackCameras(backCams);
    } catch (error) {
      console.warn("Could not enumerate cameras:", error);
    }
  }, [isMobileDevice]);

  // const ROMANIAN_ID_ASPECT_RATIO = 1.58;
  const ROMANIAN_ID_ASPECT_RATIO = 1.5;
  const LANDSCAPE_ID_ASPECT_RATIO = 0.67; // Inverted for landscape orientation

  // Calculate rectangle based on device orientation
  const isLandscapeMode = isMobileDevice && showCamera;

  const rectangle = isLandscapeMode
    ? {
        width: 0.85,
        height: 0.85 * LANDSCAPE_ID_ASPECT_RATIO,
        x: 0.075,
        y: (1 - 0.85 * LANDSCAPE_ID_ASPECT_RATIO) / 2,
      }
    : {
        width: 0.75,
        height: 0.85 / ROMANIAN_ID_ASPECT_RATIO,
        x: 0.125,
        y: (1 - 0.85 / ROMANIAN_ID_ASPECT_RATIO) / 2,
      };

  // Handle screen orientation locking for mobile devices
  useEffect(() => {
    const lockOrientation = async () => {
      if (
        openModal &&
        showCamera &&
        isMobileDevice &&
        typeof window !== "undefined"
      ) {
        try {
          // Check if Screen Orientation API is supported
          // eslint-disable-next-line no-restricted-globals
          const screenOrientation = screen.orientation;
          if (
            screenOrientation &&
            typeof (screenOrientation as any).lock === "function"
          ) {
            await (screenOrientation as any).lock("landscape-primary");
          }
        } catch (error) {
          console.warn("Could not lock screen orientation:", error);
          // Gracefully handle browsers that don't support orientation locking
        }
      }
    };

    const unlockOrientation = () => {
      try {
        // eslint-disable-next-line no-restricted-globals
        const screenOrientation = screen.orientation;
        if (
          screenOrientation &&
          typeof (screenOrientation as any).unlock === "function"
        ) {
          (screenOrientation as any).unlock();
        }
      } catch (error) {
        console.warn("Could not unlock screen orientation:", error);
      }
    };

    if (openModal && showCamera && isMobileDevice) {
      lockOrientation();
    } else if (!showCamera || !openModal) {
      unlockOrientation();
    }

    // Cleanup on unmount
    return () => {
      unlockOrientation();
    };
  }, [openModal, showCamera]);

  // Updated takePicture with better focus trigger
  const takePicture = async () => {
    if (isMobileDevice) {
      // Trigger focus adjustment
      await handleTapToFocus();
      // Longer delay for Samsung devices to adjust focus
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc && webcamRef.current) {
      const webcamElement = webcamRef.current.video;
      const containerInfo = {
        width: webcamElement?.clientWidth || 600,
        height: webcamElement?.clientHeight || (isMobileDevice ? 320 : 450),
      };
      const croppedImage = await cropCameraImage(
        imageSrc,
        rectangle,
        containerInfo
      );
      if (documentType === ScanDocumentType.DRIVER_LICENSE) {
        await handleProcessImage(croppedImage as string);
      } else {
        setImage(croppedImage);
        setShowCamera(false);
      }
    }
  };

  const cropCameraImage = (imageSrc: any, rect: any, containerInfo: any) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const actualWidth = img.width;
        const actualHeight = img.height;
        const containerWidth = containerInfo.width;
        const containerHeight = containerInfo.height;

        const imageAspectRatio = actualWidth / actualHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let scaledWidth,
          scaledHeight,
          offsetX = 0,
          offsetY = 0;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider - height fills container, width overflows
          scaledHeight = actualHeight;
          scaledWidth = actualHeight * containerAspectRatio;
          offsetX = (actualWidth - scaledWidth) / 2;
        } else {
          // Image is taller - width fills container, height overflows
          scaledWidth = actualWidth;
          scaledHeight = actualWidth / containerAspectRatio;
          offsetY = (actualHeight - scaledHeight) / 2;
        }

        // Calculate crop coordinates based on the visible area
        const cropX = offsetX + rect.x * scaledWidth;
        const cropY = offsetY + rect.y * scaledHeight;
        const cropWidth = rect.width * scaledWidth;
        const cropHeight = rect.height * scaledHeight;

        // Set canvas size to match crop dimensions
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        if (!ctx) return;
        ctx.drawImage(
          img,
          cropX,
          cropY,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight
        );

        resolve(canvas.toDataURL("image/jpeg", 1.0)); // Maximum quality
      };
    });
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (!e.target) return;
        const uploadedImage = e.target.result as string;
        if (documentType === ScanDocumentType.DRIVER_LICENSE || (documentType === ScanDocumentType.ID && isNewIdModel)) {
          handleProcessImage(uploadedImage);
        } else {
          setImage(uploadedImage);
          setShowCamera(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  const videoConstraints = {
    width: { ideal: isMobileDevice ? 1920 : 1280 },
    height: { ideal: isMobileDevice ? 1080 : 720 },
    // Use specific deviceId for back cameras on mobile, otherwise use facingMode
    ...(selectedDeviceId && cameraSide === "environment"
      ? { deviceId: { exact: selectedDeviceId } }
      : { facingMode: cameraSide }),
    aspectRatio: isMobileDevice ? 16 / 9 : 4 / 3,
    // Remove the advanced constraints from here - we'll apply them after camera loads
  } as any;

  const handleUserMedia = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);

    // Enumerate cameras after permission is granted so labels and deviceIds are real
    if (isMobileDevice) enumerateCameras();
  }, [enumerateCameras, isMobileDevice]);

  const handleUserMediaError = useCallback((error: any) => {
    console.error("Camera error:", error);
    setCameraReady(false);

    if (
      error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError"
    ) {
      setCameraError(Vocabulary.cameraPermissionDenied);
    } else if (
      error.name === "NotFoundError" ||
      error.name === "DevicesNotFoundError"
    ) {
      setCameraError(Vocabulary.cameraNotFound);
    } else {
      setCameraError(`Camera error: ${error.message || "Unknown error"}`);
    }
  }, []);

  // Cycle through back cameras
  const cycleBackCamera = useCallback(() => {
    if (backCameras.length <= 1) return;

    const nextIndex = (currentBackCameraIndex + 1) % backCameras.length;
    setCurrentBackCameraIndex(nextIndex);
    setSelectedDeviceId(backCameras[nextIndex].deviceId);

    // Reset camera ready state to reinitialize with new camera
    setCameraReady(false);
  }, [backCameras, currentBackCameraIndex]);

  // Tap to focus functionality for mobile devices - focuses on NEAR objects
  const handleTapToFocus = useCallback(async () => {
    if (!isMobileDevice || !webcamRef.current?.video) return;

    try {
      const videoTrack = webcamRef.current.video.srcObject as MediaStream;
      const track = videoTrack?.getVideoTracks()[0];

      if (track && "getCapabilities" in track) {
        const capabilities = track.getCapabilities() as any;
        const constraints: any = { advanced: [] };

        console.log("Camera capabilities:", capabilities); // Debug log

        // Priority 1: Try continuous autofocus (works best on most Android devices)
        if (capabilities.focusMode?.includes("continuous")) {
          constraints.advanced.push({ focusMode: "continuous" });
        } else if (capabilities.focusMode?.includes("single-shot")) {
          constraints.advanced.push({ focusMode: "single-shot" });
        }

        // Priority 2: Set focus distance for close objects
        if (capabilities.focusDistance) {
          const { min = 0, max = 1 } = capabilities.focusDistance;

          // For ID scanning, we want focus at ~20-30cm
          // Most devices use 0-1 range where higher = closer
          // Samsung devices often need values between 0.15-0.35 for close focus
          const closeDistance = min + (max - min) * 0.3;

          constraints.advanced.push({
            focusDistance: { ideal: closeDistance, min: min, max: max },
          });
        }

        // Apply constraints
        await track.applyConstraints(constraints);

        // Give camera time to adjust
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    } catch (error) {
      console.warn("Focus adjustment failed:", error);
    }
  }, []);

  // Automatically set close focus when camera is ready
  // Improved camera ready handler with multiple focus attempts
  useEffect(() => {
    if (cameraReady && isMobileDevice) {
      let attempts = 0;
      const maxAttempts = 3;

      const applyFocus = async () => {
        attempts++;
        await handleTapToFocus();

        // Retry if we haven't reached max attempts
        if (attempts < maxAttempts) {
          setTimeout(applyFocus, 1000);
        }
      };

      // Initial delay to ensure camera is fully initialized
      const timer = setTimeout(() => {
        applyFocus();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [cameraReady, cameraSide, handleTapToFocus]); // Add cameraSide to deps

  // Reset focus when camera side changes
  useEffect(() => {
    if (cameraReady) {
      setCameraReady(false);
      // Camera will re-initialize and trigger the focus setup again
      setTimeout(() => {
        // The onUserMedia callback will set cameraReady to true again
      }, 100);
    }
  }, [cameraSide]);

  // Detect if a card is placed in the guide area
  useEffect(() => {
    if (!cameraReady || documentType !== ScanDocumentType.DRIVER_LICENSE) {
      setIsCardDetected(false);
      return;
    }
    const detectCard = () => {
      const video = webcamRef.current?.video;
      if (!video || video.readyState < 2) return;
      const W = 120, H = 75;
      const canvas = document.createElement("canvas");
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, W, H);
      const rx = Math.floor(rectangle.x * W);
      const ry = Math.floor(rectangle.y * H);
      const rw = Math.max(1, Math.floor(rectangle.width * W));
      const rh = Math.max(1, Math.floor(rectangle.height * H));
      const { data } = ctx.getImageData(rx, ry, rw, rh);
      const n = data.length / 4;
      let sumL = 0, sumLSq = 0;
      for (let i = 0; i < data.length; i += 4) {
        const lum = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
        sumL += lum;
        sumLSq += lum * lum;
      }
      const mean = sumL / n;
      const variance = sumLSq / n - mean * mean;
      setIsCardDetected(variance > 0.02 && mean > 0.25);
    };
    const id = setInterval(detectCard, 300);
    return () => clearInterval(id);
  }, [cameraReady, documentType, rectangle]);

  const handleProcessImage = async (image: string) => {
    try {
      if (documentType === ScanDocumentType.DRIVER_LICENSE) {
        if (!frontData) {
          setFrontData(image);
          setFrontCapturedPreview(image);
          setImage(null);
          setShowCamera(false);
          setTimeout(() => {
            setFrontCapturedPreview(null);
            setIsProcessingBack(true);
            setShowCamera(true);
          }, 2000);
        } else {
          setIsModalOpen(true);
          const result = await processDrivingLicenseBothSides(frontData, image);
          if (result && result.code === 200) {
            setIsModalOpen(false);
            doneProcess(result);
            setImage(null);
            setShowCamera(true);
            setFrontData(null);
            setIsProcessingBack(false);
          } else {
            throw new Error("Back processing failed");
          }
        }
      } else if (documentType === ScanDocumentType.ID && isNewIdModel) {
        setIsModalOpen(true);
        if (!frontData) {
          const frontResult = await processImage(image, ScanDocumentType.ID);
          if (frontResult && frontResult.code === 200) {
            setFrontData(frontResult);
            setIsProcessingBack(true);
            setImage(null);
            setShowCamera(true);
            setIsModalOpen(false);
            notifySuccess(Vocabulary.idFrontSideProcessedSuccessfully);
          } else {
            throw new Error("Front processing failed");
          }
        } else {
          const backResult = await processImage(image, ScanDocumentType.ID_BACK);
          if (backResult && backResult.code === 200) {
            const combinedResult = {
              status: frontData.status,
              code: frontData.code,
              result: { ...frontData.result, ...backResult.result },
              image: [...(frontData.image || []), ...(backResult.image || [])],
            };
            setIsModalOpen(false);
            doneProcess(combinedResult);
            setImage(null);
            setShowCamera(true);
            setFrontData(null);
            setIsProcessingBack(false);
            notifySuccess(Vocabulary.imageProcessedSuccessfully);
          } else {
            throw new Error("Back processing failed");
          }
        }
      } else {
        setIsModalOpen(true);
        const result = documentType === ScanDocumentType.ID
          ? await processIdentityCard(image)
          : await processImage(image, documentType);
        if (result && result.code === 200) {
          setIsModalOpen(false);
          doneProcess(result);
          setImage(null);
          setShowCamera(true);
          notifySuccess(Vocabulary.imageProcessedSuccessfully);
        } else {
          throw new Error("Processing failed");
        }
      }
    } catch (error) {
      setIsModalOpen(false);
      console.error("Error processing image:", error);
      if (documentType === ScanDocumentType.DRIVER_LICENSE || (documentType === ScanDocumentType.ID && isNewIdModel)) {
        handleModalClose();
      }
    }
  };

  const resetCamera = () => {
    setImage(null);
    setShowCamera(true);
  };

  const handleModalClose = () => {
    setFrontData(null);
    setFrontCapturedPreview(null);
    setIsProcessingBack(false);
    setIsNewIdModel(false);
    resetCamera();
    handleClose();
  };

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleModalClose}
        maxWidth={isMobileDevice ? false : "md"}
        fullWidth={!isMobileDevice}
        fullScreen={isMobileDevice}
        PaperProps={{
          sx: {
            borderRadius: isMobileDevice ? 0 : "16px",
            maxHeight: isMobileDevice ? "100vh" : "90vh",
            margin: isMobileDevice ? 0 : { xs: "16px", sm: "32px" },
            height: isMobileDevice ? "100vh" : "auto",
          },
        }}
      >
        {/* Landscape mode - full screen camera */}
        {isLandscapeMode ? (
          <div className={styles.landscapeWrapper}>
            <div
              className={`${styles.cameraContainer} ${styles.cameraContainerLandscape}`}
              onClick={handleTapToFocus}
            >
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                forceScreenshotSourceSize
                className={`${styles.webcam} ${styles.webcamLandscape}`}
                playsInline
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                screenshotQuality={1.0}
                mirrored={cameraSide === "user"}
              />

              {/* Driving licence guide overlay */}
              {documentType === ScanDocumentType.DRIVER_LICENSE && (
                <div className={`${styles.cardGuide} ${isCardDetected ? styles.cardGuideDetected : ""}`}>
                  <div className={styles.cornerTL} />
                  <div className={styles.cornerTR} />
                  <div className={styles.cornerBL} />
                  <div className={styles.cornerBR} />
                </div>
              )}

              {/* Close button */}
              <button className={styles.closeButton} onClick={handleModalClose}>
                <CloseIcon />
              </button>

              {/* Camera switch button */}
              {/* <button
                className={styles.cycleBackCameraButton}
                onClick={() => setCameraSide(cameraSide === "user" ? "environment" : "user")}
              >
                <AutorenewIcon />
              </button> */}

              {/* Cycle back cameras button - only show when using back camera and multiple cameras available */}
              {cameraSide === "environment" && backCameras.length > 1 && (
                <Button
                  className={styles.cameraSwitchButton}
                  onClick={cycleBackCamera}
                  startIcon={<FlipCameraAndroidIcon />}
                  sx={{
                    position: 'absolute',
                    top: '50px',
                    right: '20px',
                    zIndex: 5,
                  }}
                >
                  {Vocabulary.cycleCameras}
                </Button>
              )}

              {/* Front/Back indicator for landscape */}
              {documentType === ScanDocumentType.DRIVER_LICENSE && (
                <div className={styles.landscapeSideIndicator}>
                  {isProcessingBack
                    ? Vocabulary.fitYourDriverLicenseBackInSquare
                    : Vocabulary.fitYourDriverLicenseFrontInSquare}
                </div>
              )}

              {/* In-camera capture button */}
              {cameraReady && !cameraError && (
                <div className={styles.captureButton} onClick={takePicture}>
                  <CameraEnhanceIcon sx={{ fontSize: 36, color: "#4CAF50" }} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`${styles.portraitWrapper} ${
              !isMobileDevice ? styles.portraitWrapperDesktop : ""
            }`}
          >
            {/* Header Row */}
            <div className={styles.headerRow}>
              <button className={styles.headerCloseButton} onClick={handleModalClose}>
                <CloseIcon fontSize="small" />
              </button>
              <h2 className={styles.header}>
                {documentType === ScanDocumentType.DRIVER_LICENSE
                  ? isProcessingBack
                    ? Vocabulary.fitYourDriverLicenseBackInSquare
                    : Vocabulary.fitYourDriverLicenseFrontInSquare
                  : Vocabulary.takeAPhoto}
              </h2>
              <div className={styles.headerSpacer} />
            </div>

            {/* Step Indicator - Driver License only */}
            {documentType === ScanDocumentType.DRIVER_LICENSE && (
              <div className={styles.stepIndicator}>
                <div className={styles.stepDotActive}>1</div>
                <div className={`${styles.stepConnector} ${isProcessingBack ? styles.stepConnectorActive : ""}`} />
                <div className={isProcessingBack ? styles.stepDotActive : styles.stepDotInactive}>2</div>
              </div>
            )}

            {/* New ID model toggle */}
            {documentType === ScanDocumentType.ID && !isProcessingBack && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isNewIdModel}
                    onChange={(e) => setIsNewIdModel(e.target.checked)}
                    color="primary"
                  />
                }
                label={Vocabulary.newIdCardModel}
                sx={{ mb: 1, alignSelf: "center" }}
              />
            )}

            {/* Camera */}
            {showCamera && (
              <div className={styles.cameraContainer} onClick={handleTapToFocus}>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  forceScreenshotSourceSize
                  className={`${styles.webcam} ${isMobileDevice ? styles.webcamMobile : ""}`}
                  playsInline
                  onUserMedia={handleUserMedia}
                  onUserMediaError={handleUserMediaError}
                  screenshotQuality={1.0}
                  mirrored={cameraSide === "user"}
                />

                {/* Driving licence guide overlay */}
                {documentType === ScanDocumentType.DRIVER_LICENSE && (
                  <div className={`${styles.cardGuide} ${isCardDetected ? styles.cardGuideDetected : ""}`}>
                    <div className={styles.cornerTL} />
                    <div className={styles.cornerTR} />
                    <div className={styles.cornerBL} />
                    <div className={styles.cornerBR} />
                  </div>
                )}

                {cameraError && (
                  <div className={styles.cameraErrorOverlay}>{cameraError}</div>
                )}

                {cameraReady && !cameraError && (
                  <>
                    {documentType !== ScanDocumentType.DRIVER_LICENSE && (
                      <div className={`${styles.instructions} ${documentType === ScanDocumentType.ID && isNewIdModel ? styles.instructionsDriverLicense : ""}`}>
                        {documentType === ScanDocumentType.ID && isNewIdModel
                          ? isProcessingBack ? Vocabulary.fitYourIdBackInSquare : Vocabulary.fitYourIdFrontInSquare
                          : Vocabulary.fitYourImageInSquare}
                      </div>
                    )}

                    <div
                      className={styles.captureButton}
                      onClick={(e) => { e.stopPropagation(); takePicture(); }}
                    >
                      <CameraEnhanceIcon sx={{ fontSize: 36, color: "#4CAF50" }} />
                    </div>

                    <button
                      className={styles.uploadOverlayButton}
                      onClick={(e) => { e.stopPropagation(); document.getElementById("upload-file")?.click(); }}
                    >
                      <UploadFileIcon fontSize="small" />
                    </button>

                    {isMobileDevice && (
                      <button
                        className={styles.cameraSwitchOverlayButton}
                        onClick={(e) => { e.stopPropagation(); setCameraSide(cameraSide === "user" ? "environment" : "user"); }}
                      >
                        <AutorenewIcon fontSize="small" />
                      </button>
                    )}

                    {isMobileDevice && cameraSide === "environment" && backCameras.length > 1 && (
                      <button
                        className={styles.cycleCamerasOverlayButton}
                        onClick={(e) => { e.stopPropagation(); cycleBackCamera(); }}
                      >
                        <FlipCameraAndroidIcon fontSize="small" />
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Hidden File Input */}
            <input
              accept="image/*"
              className={styles.hiddenInput}
              id="upload-file"
              type="file"
              onChange={handleFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
            />

            {/* Upload button for DRIVER_LICENSE (always visible below camera) */}
            {documentType === ScanDocumentType.DRIVER_LICENSE && showCamera && (
              <Button
                variant="contained"
                size="large"
                startIcon={<UploadFileIcon />}
                onClick={() => document.getElementById("upload-file")?.click()}
                className={styles.driverLicenseUploadButton}
              >
                {Vocabulary.uploadPhoto}
              </Button>
            )}

            {/* Image Preview (non-DRIVER_LICENSE) */}
            {image && (
              <Paper elevation={2} className={styles.previewContainer}>
                <h3 className={styles.previewTitle}>{Vocabulary.photoTaken}</h3>
                <img
                  src={image}
                  alt="Preview"
                  className={`${styles.previewImage} ${isMobileDevice ? styles.previewImageMobile : ""}`}
                />
                <div className={styles.previewButtonContainer}>
                  <Button variant="outlined" onClick={resetCamera} startIcon={<RefreshIcon />} className={styles.button}>
                    {Vocabulary.cancel}
                  </Button>
                  <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={() => handleProcessImage(image)} className={`${styles.button} ${styles.buttonPrimary}`}>
                    {Vocabulary.process}
                  </Button>
                </div>
              </Paper>
            )}

            {/* Action buttons (non-DRIVER_LICENSE only) */}
            {documentType !== ScanDocumentType.DRIVER_LICENSE && (
              <div className={styles.actionButtons}>
                <Button variant="outlined" onClick={handleModalClose} startIcon={<CloseIcon />} className={styles.button}>
                  {Vocabulary.close}
                </Button>
                {showCamera && (
                  <>
                    <Button variant="contained" startIcon={<CameraEnhanceIcon />} onClick={takePicture} className={`${styles.button} ${styles.buttonPrimary}`}>
                      {Vocabulary.takePhoto}
                    </Button>
                    <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => document.getElementById("upload-file")?.click()} className={styles.button}>
                      {Vocabulary.uploadPhoto}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Processing Dialog */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{
          className: styles.processingDialog,
        }}
      >
        <Grid container direction="column" alignItems="center" spacing={3}>
          <Grid item>
            <div className={styles.processingIconContainer}>
              <CameraEnhanceIcon sx={{ fontSize: 32, color: "#009846" }} />
            </div>
          </Grid>

          <Grid item>
            <h3 className={styles.processingTitle}>
              {Vocabulary.imageIsProcessing}
            </h3>
            <p className={styles.processingSubtitle}>
              {Vocabulary.pleaseWaitProcessingYourPhoto}
            </p>

            <CircularProgress size={40} sx={{ color: "#009846" }} />
          </Grid>
        </Grid>
      </Dialog>

      {/* Front captured feedback overlay */}
      {frontCapturedPreview && (
        <div className={styles.frontCapturedOverlay}>
          <img src={frontCapturedPreview} alt="front" className={styles.frontCapturedImage} />
          <div className={styles.frontCapturedBadge}>
            <svg viewBox="0 0 24 24" className={styles.frontCapturedCheck}>
              <polyline points="20 6 9 17 4 12" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className={styles.frontCapturedText}>Față capturată! Pregătește spatele...</p>
        </div>
      )}
    </>
  );
}
